import { styled } from '@mui/material/styles';
import { Alert, Button, InputAdornment, Table, TableCell, TableContainer, TableHead, TextField, ToggleButtonGroup } from '@mui/material';

// Optically center the glyphs, rather than only their line box.
const opticalCenter = { transform: 'translateY(2px)' } as const;

export const StyledSection = styled('section')(({ theme }) => ({
    marginTop: theme.spacing(2),
    width: '100%',
    maxWidth: 800,
}));

export const StyledHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
}));

export const StyledUnitToggleGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    width: 248,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 0,
    padding: 0,
    backgroundColor: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: Number(theme.shape.borderRadius) * 5,
    '& .MuiToggleButtonGroup-grouped': {
        flex: '1 1 0',
        minWidth: 0,
        height: 34,
        margin: '0 !important',
        border: '0 !important',
        borderRadius: '20px !important',
        textTransform: 'none',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        letterSpacing: 0,
        '&.Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:hover': {
                backgroundColor: theme.palette.primary.dark,
            },
        },
    },
}));

export const StyledUnitLabel = styled('span')({
    display: 'grid',
    placeItems: 'center',
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    margin: 0,
    padding: 0,
    lineHeight: 1,
    letterSpacing: 0,
    textAlign: 'center',
    textIndent: 0,
    fontSize: '1.125rem',
    ...opticalCenter,
});

export const StyledAdornmentUnitLabel = styled(StyledUnitLabel)(({ theme }) => ({
    color: theme.palette.text.primary,
}));

export const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '10px',
    overflow: 'hidden',
}));

export const StyledTable = styled(Table)(({ theme }) => ({
    tableLayout: 'fixed',
    '& th, & td': {
        padding: theme.spacing(1.5),
        [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(1.5, 3),
        },
    },
    '& th + th, & td': {
        borderLeft: `1px solid ${theme.palette.divider}`,
    },
    '& tr:last-child td, & tr:last-child th': {
        borderBottom: 0,
    },
}));

export const StyledTableHead = styled(TableHead)(({ theme }) => ({
    backgroundColor: theme.palette.action.hover,
}));

export const StyledHeaderCell = styled(TableCell)({
    verticalAlign: 'middle',
});

export const StyledLevelCell = styled(TableCell)({
    verticalAlign: 'middle',
    fontSize: '1rem',
});

export const StyledCellText = styled('span')({
    display: 'block',
    ...opticalCenter,
});

export const StyledTightCellText = styled(StyledCellText)({
    lineHeight: 1,
});

export const StyledThresholdTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        paddingRight: 0,
        overflow: 'hidden',
        backgroundColor: theme.palette.action.hover,
    },
    '& .MuiOutlinedInput-input': {
        flex: '1 1 0',
        minWidth: 0,
        width: 0,
        backgroundColor: theme.palette.background.paper,
        borderRadius: '8px',
        borderRight: `1px solid ${theme.palette.divider}`,
    },
}));

export const StyledUnitAdornment = styled(InputAdornment, {
    shouldForwardProp: (prop) => prop !== '$wide',
})<{ $wide: boolean }>(({ $wide }) => ({
    alignSelf: 'stretch',
    height: 'auto',
    maxHeight: 'none',
    margin: 0,
    width: $wide ? 80 : 50,
    flexShrink: 0,
    padding: 0,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

export const StyledOrderAlert = styled(Alert)(({ theme }) => ({
    marginTop: theme.spacing(1),
    alignItems: 'center',
    '& .MuiAlert-icon': {
        paddingTop: 0,
        paddingBottom: 0,
        alignItems: 'center',
    },
    '& .MuiAlert-message': {
        paddingTop: theme.spacing(0.75),
        paddingBottom: theme.spacing(0.75),
        ...opticalCenter,
    },
}));

export const StyledActions = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1.5),
}));

export const StyledSubmitButton = styled(Button)(({ theme }) => ({
    borderRadius: '8px',
    textTransform: 'none',
    fontSize: '1.125rem',
    padding: theme.spacing(1.5, 4),
    minHeight: 44,
    alignItems: 'center',
}));
